import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, useLocation } from "react-router-dom";

import { MantineProvider, createTheme } from "@mantine/core";
import { DatesProvider } from "@mantine/dates";

import dayjs from "dayjs";
import "dayjs/locale/pt-br";

import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";

import Header from "./Layout/Header/Header";
import AppRoutes from "./routes/AppRoutes";
import { DashboardFiltersProvider } from "./context/DashboardFiltersContext";
import { ExportModeContext } from "./context/ExportModeContext";
import "./App.css";

dayjs.locale("pt-br");

const theme = createTheme({
  primaryColor: "violet",
  defaultRadius: "md",
  fontFamily: "Mulish, system-ui, Arial, sans-serif",
});

function AppContent() {
  const location = useLocation();
  const isErrorPage = location.pathname === "/error";
  const hideHeader = isErrorPage;

  const [isExporting, setIsExporting] = useState(
    document.body.classList.contains("is-exporting")
  );

  const [isExportingData, setIsExportingData] = useState(false);

  useEffect(() => {
    const sync = () =>
      setIsExporting(document.body.classList.contains("is-exporting"));

    window.addEventListener("export-mode-change", sync);
    return () => window.removeEventListener("export-mode-change", sync);
  }, []);

  useEffect(() => {
    const handleDashboardLoading = (event) => {
      setIsExportingData(Boolean(event?.detail?.loading));
    };

    window.addEventListener("dashboard-loading-change", handleDashboardLoading);

    return () => {
      window.removeEventListener("dashboard-loading-change", handleDashboardLoading);
    };
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("theme") || "light";
    document.body.classList.toggle("dark-theme", saved === "dark");
  }, []);

  return (
    <>
      <ExportModeContext.Provider value={isExporting}>
        <div className="main-content">
          {!hideHeader && <Header exportDisabled={isExportingData} />}

          <div className="pages-container">
            <AppRoutes />
          </div>
        </div>
      </ExportModeContext.Provider>
    </>
  );
}

export default function App() {
  const [scheme, setScheme] = useState(() =>
    localStorage.getItem("theme") === "dark" ? "dark" : "light"
  );

  useEffect(() => {
    const onThemeChange = () => {
      setScheme(localStorage.getItem("theme") === "dark" ? "dark" : "light");
    };
    window.addEventListener("theme-change", onThemeChange);

    const onStorage = (e) => {
      if (e.key === "theme") {
        setScheme(e.newValue === "dark" ? "dark" : "light");
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("theme-change", onThemeChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    const id = "mulish-font";
    if (document.getElementById(id)) return;

    const link = document.createElement("link");
    link.id = id;
    link.href =
      "https://fonts.googleapis.com/css2?family=Mulish:wght@400;500;600;700;800&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }, []);

  return (
    <MantineProvider theme={theme} forceColorScheme={scheme}>
      <DatesProvider
        settings={{
          locale: "pt-br",
          firstDayOfWeek: 0,
          weekendDays: [0, 6],
          timezone: "America/Sao_Paulo",
        }}
      >
        <DashboardFiltersProvider>
          <Router>
            <AppContent />
          </Router>
        </DashboardFiltersProvider>
      </DatesProvider>
    </MantineProvider>
  );
}
